/**
 * alertSourceLink.ts — Spec 193 (Alert Escalation Engine)
 *
 * Resolves the in-app route for an alert's SOURCE entity, so the header bell and
 * the /alerts table can deep-link a click straight to the thing the alert is about.
 *
 * Route matrix verified (workflow-mapped) against AppRoutes.tsx: the detail routes
 * genuinely read :id via useParams and render the specific entity for
 * capacity / lakehouse / warehouse / storage_account. predictability + pipeline land
 * on the pipeline-monitoring section (predictability filters by app_instance);
 * collection-job on the jobs dashboard; workspace on the workspace list (no detail route).
 *
 * Returns null when the alert has no resolvable source (older alerts with no entity_id,
 * or an unmapped type) — callers should render such alerts as non-clickable.
 */

export interface AlertSourceRef {
  entityType?: string | null;
  entityId?: string | null;
  resourceType?: string | null;
  /**
   * The collection STAGE the alert is about (`"<stage>"` or `"<stage>/<unit>"`), served as
   * `sourceScope`. It is the only field that says which of the two Spec-265 integrity
   * screens owns the alert: `entity_id` is a derived GUID (`appInstanceToGuid`) that cannot
   * be read back, and `entityName` resolves to the literal string `'collection_problem'`
   * for every one of them because the junction type has no display-name arm.
   */
  sourceScope?: string | null;
}

/**
 * Spec 265 owns TWO screens, and an alert belongs to exactly one of them.
 *
 * Infrastructure Sync is the tone-setter: what it cannot see never reaches the entity
 * tables at all, so its failures are diagnosed there alongside the access-gap
 * cross-check. Everything else is diagnosed on Collection Jobs. Sending an operator to
 * the wrong one costs them the click the deep-link was supposed to save, and worse,
 * lands them on a screen where the problem genuinely is not listed — which reads as the
 * alert being stale rather than as a routing mistake.
 *
 * Same predicate as the backend route scoping, deliberately: if the two ever disagree,
 * a link lands on a screen that filters the alert out.
 */
const isInfraStage = (stage: string): boolean =>
  stage.includes('infra') || stage.includes('sync');

export function alertSourceLink(ref: AlertSourceRef): string | null {
  const entityType = (ref.entityType || '').toLowerCase();
  const id = (ref.entityId || '').trim();
  const resourceType = (ref.resourceType || '').toLowerCase();
  const sourceScope = (ref.sourceScope || '').toLowerCase();

  // 0) Spec 265 (FR-025b) — collection integrity. These were the ONLY engine alerts with
  //    no entry here at all, so every one of them rendered non-clickable: the platform
  //    could tell an operator that collection was silently authenticating to nothing, and
  //    then leave them to find the right screen themselves. That is the same gap the spec
  //    exists to close, one layer up.
  switch (resourceType) {
    case 'collection-access-gap':
      // The billing cross-check that produced the finding lives ONLY on Infrastructure
      // Sync, so this one never routes to Collection Jobs regardless of stage.
      return '/discovery';
    case 'collection-auth':
    case 'collection-credential-expiry':
    case 'collection-completeness':
    case 'collection-silence':
    case 'collection-abnormal-termination':
      return isInfraStage(sourceScope) ? '/discovery' : '/jobs';
    default:
      break;
  }

  // 1) Engine resource types whose entity isn't a plain dbo detail route.
  switch (resourceType) {
    case 'predictability':
      return id
        ? `/operations/pipeline-monitoring?tab=activities&app=${encodeURIComponent(id)}`
        : '/operations/pipeline-monitoring';
    case 'pipeline':
      return '/operations/pipeline-monitoring';
    case 'collection-job':
      return '/jobs';
    // Spec 252 (FR-008): a backup alert must land on the ERROR DETAIL + its remediation, not on a
    // generic entity page where the operator still cannot see what failed. Both the header
    // NotificationBell and /alerts go through THIS function, so both deep-link from one change.
    case 'backup-run':
    case 'backup-readiness':
      // 🛑 entityId, NOT entity. `?entity=` was consumed as a free-text `q` LIKE over
      // entity_name / raw_detail / stored_code — none of which a GUID can match — so every
      // backup alert landed on the "No backup errors" empty state. Measured on dev: 0 hits for
      // an entity holding 2,709 real errors. `?entityId=` is an exact match on e.entity_id.
      return id
        ? `/backup/errors?entityId=${encodeURIComponent(id)}`
        : '/backup/errors';
    default:
      break;
  }

  // 2) Otherwise route by the source entity type → its detail page (reads :id).
  switch (entityType) {
    case 'capacity':
      return id ? `/capacity-metrics/${id}` : null;
    case 'lakehouse':
      return id ? `/lakehouses/${id}` : null;
    case 'warehouse':
      return id ? `/warehouses/${id}` : null;
    case 'storage_account':
      return id ? `/storage-accounts/${id}` : null;
    case 'workspace':
      return '/workspaces'; // no per-workspace detail route yet — land on the list
    // Pipeline/job-shaped sources (legacy standalone writers set these entity_types;
    // engine alerts hit the resource_type cases above). No per-row route → section landing.
    case 'predictability_finding':
    case 'pipeline':
    case 'monitored_pipeline':
    case 'job':
      return '/operations/pipeline-monitoring';
    default:
      return null;
  }
}
